// exporting - the mode the report renders in while it is being turned into a PDF.
//
// Export PDF is the browser's own print-to-PDF, and for most of this app that
// would be a stylesheet's job. It cannot be here, because the report's content
// is conditionally MOUNTED rather than merely hidden: one pane at a time, folds
// that render `{open && children}`, a virtualised event stream that keeps ~25
// rows in the DOM whatever the run size, tables sliced to their first page
// behind a "Show all", and per-dump fetches that only fire on a click. A
// `@media print` rule can reveal what is hidden; it cannot render what was never
// mounted.
//
// So export is React state. `useExporting()` is what a component asks when the
// answer differs on paper - open the fold, drop the filter chips, render every
// row - and the print stylesheet is left with page geometry alone.
//
// The second half of this module is the readiness registry. The panes that fetch
// are covered by TanStack Query's own `useIsFetching`, which the export loop
// polls; what it does not cover is async work that is not a query - pdf.js
// rasterising a lure's pages is the case that forced this. Such a component
// calls `useExportHold(pending)` and the export waits for it rather than
// printing a blank canvas.

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface ExportScope {
  /** True while this subtree is being rendered for the printed document. */
  readonly exporting: boolean;
  /** How many components still have work outstanding. Zero means printable. */
  readonly holds: number;
  /** Registers or clears one component's outstanding work. */
  readonly setHold: (id: string, pending: boolean) => void;
}

const ExportContext = createContext<ExportScope | null>(null);

/**
 * The export scope for one export run, owned by the component driving it.
 *
 * Split from the provider so the driver can read `holds` - it is the half of
 * the readiness answer that TanStack Query does not know about.
 */
export function useExportScope(exporting: boolean): ExportScope {
  const [holds, setHolds] = useState(0);
  const pending = useRef<Set<string>>(new Set());

  const setHold = useCallback((id: string, held: boolean) => {
    const set = pending.current;
    if (held === set.has(id)) return;
    if (held) set.add(id);
    else set.delete(id);
    setHolds(set.size);
  }, []);

  // Leaving export mode clears the registry. A holder that unmounted between
  // renders without releasing - which is exactly what happens when the printed
  // document comes down mid-fetch - would otherwise hold the NEXT export open
  // until its ceiling, and the reader would see a button that hangs for twenty
  // seconds for no reason they can observe.
  useEffect(() => {
    if (exporting) return;
    pending.current.clear();
    setHolds(0);
  }, [exporting]);

  return useMemo(() => ({ exporting, holds, setHold }), [exporting, holds, setHold]);
}

/** Puts a subtree in export mode. Nothing outside it is affected. */
export function ExportProvider({ scope, children }: { scope: ExportScope; children: ReactNode }) {
  return <ExportContext.Provider value={scope}>{children}</ExportContext.Provider>;
}

/**
 * True when this component is being rendered for the printed document.
 *
 * False everywhere else, including the whole on-screen report - there is no
 * provider above it, and that is deliberate: the screen must not pay for a
 * context change every time someone exports.
 */
export function useExporting(): boolean {
  return useContext(ExportContext)?.exporting ?? false;
}

/**
 * Holds the export open while `pending` is true.
 *
 * For async work TanStack Query cannot see - a canvas still being painted, a
 * decoder still running. A no-op outside export mode, so a component can call it
 * unconditionally without asking whether it is on paper.
 */
export function useExportHold(pending: boolean): void {
  const scope = useContext(ExportContext);
  const id = useId();
  const exporting = scope?.exporting ?? false;
  const setHold = scope?.setHold;

  useEffect(() => {
    if (!exporting || !setHold) return;
    setHold(id, pending);
    return () => setHold(id, false);
  }, [exporting, setHold, id, pending]);
}
